<template>
  <div>
    <div v-if="formattedData?.length === 0" class="p-4">
      <h6>Es sind noch keine Änderungen vorhanden.</h6>
    </div>
    <div v-for="(change, i) in formattedData" :key="change.key + ' ' + change.date + '-' + i" class="my-2">
      <div class="d-flex">
        <span v-if="change.passiveText">
          {{ change.passiveText.artikel }}
          <b>{{ change.passiveText.bezeichnung }}</b>
          {{ change.passiveText.verb }}
        </span>
        <span v-else-if="change.key === 'CountryManager'">
          Es wurden
          <b>{{ change.formattedKey }}</b>
          geändert.
        </span>
        <span v-else>
          {{ change.nameOfObjectEditor }} hat
          <b>{{ change.formattedKey }}</b>
          geändert.
        </span>
        <div
          style="color: rgba(0, 0, 0, 0.5); cursor: pointer"
          class="ml-6"
          :id="'date-change' + uniqueId + '-' + change.date + '-' + i"
        >
          {{ change.formattedDate }}
        </div>
      </div>
      <div class="pl-2" style="color: gray; font-size: 0.9rem" v-if="change.additionalIdentifier">
        {{ change.additionalIdentifier }}
      </div>

      <div class="d-flex p-2 align-items-center" style="gap: 12px">
        <span class="d-flex align-items-center" v-html="change.oldValueFormatted"></span>

        <span style="font-size: 20px">&rarr;</span>

        <span v-html="change.newValueFormatted"></span>
        <b-tooltip
          :target="'date-change' + uniqueId + '-' + change.date + '-' + i"
          triggers="hover"
          placement="right"
        >
          <span style="font-size: 1rem">{{ formatDate(change.date) }}</span>
        </b-tooltip>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import { format, formatDistanceToNow, isAfter } from 'date-fns';
import de from 'date-fns/locale/de';
import { transferszenarioOptions } from '@/core/common/helpers/utils.js';
import currenciesJson from '@/core/common/helpers/currencies.json';
import zimmertypenJson from '@/core/common/helpers/zimmertypen.json';
import { lucideHtml } from '@/core/common/helpers/lucide-html';
import { Ban, Check } from 'lucide-vue';
import {
  translateEinkaufsartOptions,
  translateStatusGruppeOptions,
  reiseterminStatePillHtml,
  parseFullUsername,
} from '@/core/common/helpers/utils.js';

export default {
  props: {
    data: { type: Object },
  },
  data() {
    return {
      ignoredField: 'LastModifiedUserId',
      uniqueId: Math.random().toString(36).substring(2, 9),
      // Präfix, an dem Changes eines Plan-Zimmerkontingents erkannt werden
      zimmerkontingentIdentifierPrefix: 'Zimmerkontingent Plan: ',
      // Formulierung ohne Person für Änderungen, zu denen die API keinen Benutzer liefert
      passiveTexts: {
        State: { artikel: 'Der', bezeichnung: 'Status', verb: 'wurde geändert.' },
        Inlandsflug: { artikel: 'Der', bezeichnung: 'Inlandsflug', verb: 'wurde geändert.' },
      },
      formatKeys: {
        State: 'den Status',
        // Reisedaten
        Hotelnaechte: 'die Hotelnächte',
        KalkPax: 'die Kalk Pax',
        ForecastPax: 'die FC Pax',
        PlanPax: 'die Plan Pax',
        ErwarteteTermindurchfuehrungsquote: 'die erw. Termindurchführungsquote',
        Vorverlaengerung: 'die Vorverlängerung',
        DurchfuehrbareMinPax: 'die durchführbare Min Pax',
        MinPax: 'die Min Pax',
        MaxPax: 'die Max Pax',
        VornachtMoeglich: 'Vornacht möglich',
        EinkaufspreisVornachtDzProPerson: 'die EK kalk. Vornacht DZ p.P.',
        HinflugMaxWartezeit: 'die Max. Wartezeit Hin',
        RueckflugMaxWartezeit: 'die Max. Wartezeit Rück',
        HinflugMaxAnzahlTransfergruppen: 'die Max. Anzahl Transfergruppen Hin',
        RueckflugMaxAnzahlTransfergruppen: 'die Max. Anzahl Transfergruppen Rück',
        // Releaseinformationen
        Releasefrist: 'die Releasefrist', // Sind mehrere und werden mit komma getrennt
        // Transfer
        TransferszenarioHin: 'das Transferszenario Hin',
        TransferszenarioRueck: 'das Transferszenario Rück',
        TransferkostenWaehrung: 'die Transferkosten Währung',
        KommentarTransferszenarioHin: 'den Kommentar TS Hin',
        KommentarTransferszenarioRueck: 'den Kommentar TS Rück',
        TransferkostenHin: 'die Transferkosten Hin',
        TransferkostenRueck: 'die Transferkosten Rück',
        IsKeineTransferplanungNotwendigHin: 'Keine Transferplanung Hin',
        IsKeineTransferplanungNotwendigRueck: 'Keine Transferplanung Rück',
        MaxTransfergruppenBeachtenHin: 'die Max. Anzahl Gruppen nicht überschreiten Hin',
        MaxTransfergruppenBeachtenRueck: 'die Max. Anzahl Gruppen nicht überschreiten Rück',
        AbweichenderFlughafenHinIataCode: 'den Abweichenden Flughafen Hin',
        AbweichenderFlughafenRueckIataCode: 'den Abweichenden Flughafen Rück',
        CountryManager: 'die Country Manager',
        // Flugdaten
        Flugoption: 'die Flugoption',
        SkrFlugangebot: 'das SKR Flugangebot',
        ZiellandZielflughaefen: 'die Zielland Zielflughäfen', // wird im Verlauf noch nicht angezeigt
        ZiellandZielflughaefenPrio: 'die Zielland Zielflughäfen Prio',
        ZiellandAbflugflughaefen: 'die Zielland Abflugflughäfen', // wird im Verlauf noch nicht angezeigt
        ZiellandAbflugflughaefenPrio: 'die Zielland Abflugflughäfen Prio',
        OvernightOutbound: 'den Overnight Outbound',
        OvernightInbound: 'den Overnight Inbound',
        ZiellandZielflughafenPrio: 'die Zielland-Zielflughafen Priorität', // wird im Verlauf noch nicht angezeigt
        ZiellandAbflugFlughafenPrio: 'die Zielland-Abflugflughafen Priorität', // wird im Verlauf noch nicht angezeigt
        HotelstartVorLandung: 'den Hotelstart vor Landung',
        RueckflugNachHotelende: 'den Rückflug nach Hotelende',
        FruehesteAnkunft: 'die früheste Ankunft',
        SpaetesteAnkunft: 'die späteste Ankunft',
        FruehesterAbflug: 'den frühesten Abflug',
        SpaetesterAbflug: 'den spätesten Abflug',
        RestriktionenHinRollover: 'die Restriktionen Hin Rollover',
        RestriktionenRueckRollover: ' die Restriktionen Rück Rollover',
        ThLangstrecke: 'die TH Langstrecke',
        ThInlandsflug: 'den TH Inlandsflug',
        // Inlandsflüge (können mehrere Sein)
        Abflughafen: 'den Abflughafen',
        Zielflughafen: 'den Zielflughafen',
        Einkauf: 'den Einkauf',
        MinimaleUmsteigezeit: 'die minimale Umsteigezeit',
        ZielflughafenIataCode: 'den Zielflughafen',
        AbflughafenIataCode: 'den Abflughafen',
        InlandsFruehesterAbflug: 'den frühesten Abflug des Inlandfluges',
        InlandsSpaetesteAnkunft: 'die späteste Ankunft des Inlandfluges',
        // Reisetermindaten
        IstZusatztermin: 'den Zusatztermin',
        // Einkaufsdaten
        Statusgruppe: 'die Status Gruppe',
        Einkaufsart: 'die Einkaufsart',
        GruppePnr: 'die GRP PNR',
        EingekauftePax: 'die eingekaufte Pax',
        MindestabnahmeAirline: 'die Mindestabnahme Airline',
        // GruppeFlughaefenIataCodes: 'die GRP Flughafen',
        GruppeFlughaefen: 'die GRP Flughafen',
        FlugnummerGruppeHin: 'die Flugnr. GRP Hin',
        FlugnummerGruppeRueck: 'die Flugnr. GRP Rück',
        ObClass: 'die OB Class',
        IbClass: 'die IB Class',
        Optionsfrist: 'die Optionsfrist',
        Ticketfrist: 'die Ticketfrist',
        HauptstreckePreisNetto: 'den Preis Netto',
        InlandsflugPreis: 'den INFL Preis',
        TaxesFlug: 'die Taxes',
        // Inlandsflüge
        Flugnummer: 'die Flugnummer',
        InGruppeInkludiert: 'die in Gruppe inkludiert',
        Inlandsflug: 'den Inlandsflug',
        PlanZimmerkontingent: 'die Zimmerkontingente Plan',
      },
    };
  },
  computed: {
    ...mapState({
      users: state => state.users['users'],
    }),
    formattedData() {
      let aktuelleWaehrung = 'EUR';

      return Object.entries(this.data)
        .filter(row => new Date(row[0]).getFullYear() >= 2025)
        .reduce((acc, curr) => {
          const changeDate = curr[0];
          const { changes, lastModifiedUserId } = curr[1];

          //schaut, ob sich die Währung geändert hat
          const aenderungDerWaehrung = changes.find(change => change.key === 'TransferkostenWaehrung');
          if (aenderungDerWaehrung) {
            aktuelleWaehrung = aenderungDerWaehrung.newValue;
          }

          // Die Changes eines Plan-Zimmerkontingents werden pro Kontingent zu einem Eintrag gebündelt
          const zimmerkontingentGruppen = {};
          for (const change of changes) {
            if (!this.istZimmerkontingentChange(change)) {
              continue;
            }
            if (!zimmerkontingentGruppen[change.additionalIdentifier]) {
              zimmerkontingentGruppen[change.additionalIdentifier] = [];
            }
            zimmerkontingentGruppen[change.additionalIdentifier].push(change);
          }

          for (const [identifier, gruppe] of Object.entries(zimmerkontingentGruppen)) {
            const objectEditor = this.users.find(user => user.id === lastModifiedUserId);
            acc.push({
              oldValueFormatted: this.getZimmerkontingentFormatted(gruppe, identifier, 'oldValue'),
              newValueFormatted: this.getZimmerkontingentFormatted(gruppe, identifier, 'newValue'),
              nameOfObjectEditor: objectEditor ? parseFullUsername(objectEditor) : '-',
              // Beim Löschen liefert die API keinen Benutzer, deshalb Passivformulierung
              passiveText: objectEditor
                ? null
                : { artikel: 'Die', bezeichnung: 'Zimmerkontingente Plan', verb: 'wurden geändert.' },
              date: changeDate,
              formattedDate: this.getDateDistanceToNow(changeDate),
              key: 'PlanZimmerkontingent',
              additionalIdentifier: null,
              formattedKey: this.formatKeys.PlanZimmerkontingent,
            });
          }

          for (const change of changes) {
            if (change.key === this.ignoredField || this.istZimmerkontingentChange(change)) {
              continue;
            }

            const { newValue, oldValue, key, additionalIdentifier } = change;

            // Nur Keys anzeigen, die in formatKeys definiert sind (editierbare Felder)
            if (!this.formatKeys[key]) {
              continue;
            }
            const objectEditor = this.users.find(user => user.id === lastModifiedUserId);
            const nameOfObjectEditor = objectEditor ? parseFullUsername(objectEditor) : '-';
            const formattedDate = this.getDateDistanceToNow(changeDate);

            const currencySymbol = currenciesJson[aktuelleWaehrung]?.symbol || '€';
            const newValueFormatted = this.getValueFormatted(newValue, key, currencySymbol);
            const oldValueFormatted = this.getValueFormatted(oldValue, key, currencySymbol);

            acc.push({
              oldValue,
              oldValueFormatted,
              newValue,
              newValueFormatted,
              nameOfObjectEditor,
              // Beim Löschen eines Inlandsfluges und bei Statuswechseln durch das System
              // (z.B. Aufgelegt → Durchführung) liefert die API keinen Benutzer
              passiveText: objectEditor ? null : this.passiveTexts[key] ?? null,
              date: changeDate,
              formattedDate,
              key,
              additionalIdentifier,
              formattedKey: this.formatKeys[key],
            });
          }
          return acc;
        }, [])
        .sort((a, b) => (isAfter(new Date(b.date), new Date(a.date)) ? 1 : -1));
    },
  },
  methods: {
    getDateDistanceToNow(dateString) {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true, locale: de });
    },
    formatDate(value) {
      return format(new Date(value), 'dd.MM.yyyy  HH:mm') + ' Uhr';
    },
    istZimmerkontingentChange(change) {
      return !!change.additionalIdentifier?.startsWith(this.zimmerkontingentIdentifierPrefix);
    },
    // Zimmertyp-Code mit Klartext, gleiche Schreibweise wie in der Zimmerkontingente-Karte
    formatZimmertyp(code) {
      if (!code) return '';
      const name = zimmertypenJson[code];
      return name ? `${code} – ${name}` : code;
    },
    getZimmerkontingentFormatted(gruppe, identifier, valueKey) {
      const anlageOderLoeschung = gruppe.find(change => change.key === 'PlanZimmerkontingent');
      if (anlageOderLoeschung && anlageOderLoeschung[valueKey] === null) {
        return '<span class="kachel-badge gray">---</span>';
      }

      const getWert = key => gruppe.find(change => change.key === key)?.[valueKey] ?? null;
      const zimmertyp = identifier.slice(this.zimmerkontingentIdentifierPrefix.length);

      const anzahlEinheiten = getWert('AnzahlEinheiten');
      const teiltkontingentMit = getWert('TeiltkontingentMit');
      const anzahlGeteiltesKontingent = getWert('AnzahlGeteiltesKontingent');

      // In der Klammer stehen nur die Attribute, die sich geändert haben
      const details = [];
      if (anzahlEinheiten !== null) {
        details.push(`Anzahl: ${anzahlEinheiten}`);
      }
      if (teiltkontingentMit !== null && anzahlGeteiltesKontingent !== null) {
        details.push(`teilt ${anzahlGeteiltesKontingent} Kontingent mit ${teiltkontingentMit}`);
      } else if (teiltkontingentMit !== null) {
        details.push(`teilt Kontingent mit ${teiltkontingentMit}`);
      } else if (anzahlGeteiltesKontingent !== null) {
        details.push(`geteilte Anzahl: ${anzahlGeteiltesKontingent}`);
      }

      const label = this.formatZimmertyp(zimmertyp);
      const text = details.length > 0 ? `${label} (${details.join(', ')})` : label;
      return `<span class="kachel-badge gray">${text}</span>`;
    },
    getValueFormatted(value, key, currencySymbol) {
      if (value === null) return '<span class="kachel-badge gray">---</span>';

      if (
        [
          'HotelstartVorLandung',
          'RueckflugNachHotelende',
          'RestriktionenRueckRollover',
          'RestriktionenHinRollover',
          'VornachtMoeglich',
          'IstZusatztermin',
          'InGruppeInkludiert',
          'IsKeineTransferplanungNotwendigHin',
          'IsKeineTransferplanungNotwendigRueck',
          'MaxTransfergruppenBeachtenHin',
          'MaxTransfergruppenBeachtenRueck',
        ].includes(key)
      ) {
        if (value === false)
          return `<span class="kachel-badge gray"><span class="badge badge-danger p-1">${lucideHtml(Ban, {
            size: 12,
          })}</span></span>`;
        return `<span class="kachel-badge gray"><span class="badge badge-success p-1">${lucideHtml(Check, {
          size: 12,
        })}</span></span>`;
      }

      if (key === 'Flugoption') {
        const flugoptionValues = {
          ReiseMitFlug: 'Reise mit Flug',
          ReiseOhneFlug: 'Reise ohne Flug',
          ReiseOhneFlugOptionalBuchbar: 'Reise ohne Flug - optional buchbar',
        };
        return `<span class="kachel-badge gray">${flugoptionValues[value] || value}</span>`;
      }

      if (key === 'Einkauf') {
        const einkaufsValues = {
          0: 'SKR',
          1: 'DMC',
          2: 'SKR und DMC',
        };
        return `<span class="kachel-badge gray">${einkaufsValues[value]}</span>`;
      }

      if (['TransferkostenHin', 'TransferkostenRueck'].includes(key)) {
        return '<span class="kachel-badge gray">' + value + ' ' + currencySymbol + '</span>';
      }

      if (key === 'TransferszenarioHin' || key === 'TransferszenarioRueck') {
        return '<span class="kachel-badge gray">' + transferszenarioOptions[value].text + '</span>';
      }

      if (key === 'Statusgruppe') {
        return '<span class="kachel-badge gray">' + translateStatusGruppeOptions(value) + '</span>';
      }

      if (key === 'Einkaufsart') {
        return '<span class="kachel-badge gray">' + translateEinkaufsartOptions(value) + '</span>';
      }

      if (key === 'OvernightOutbound' || key === 'OvernightInbound') {
        if (value === 0) return '<span class="kachel-badge gray">0</span>';
        if (value === 1) return '<span class="kachel-badge gray">+1</span>';
        if (value === 2) return '<span class="kachel-badge gray">+2</span>';
        return '<span class="kachel-badge gray">' + value + '</span>';
      }

      if (['KommentarTransferszenarioHin', 'KommentarTransferszenarioRueck'].includes(key)) {
        return value;
      }

      if (
        [
          'EinkaufspreisVornachtDzProPerson',
          'SkrFlugangebot',
          'ThLangstrecke',
          'ThInlandsflug',
          'HauptstreckePreisNetto',
          'InlandsflugPreis',
          'TaxesFlug',
        ].includes(key)
      ) {
        return '<span class="kachel-badge gray">' + value + '€' + '</span>';
      }

      if (key === 'Releasefrist') {
        return (
          '<span class="kachel-badge gray">' +
          String(value).replace(/[[\]]/g, '').split(',').join(', ') +
          '</span>'
        );
      }

      if (key === 'CountryManager') {
        const parsedValue = typeof value === 'string' ? JSON.parse(value || '[]') : value;
        const displayValue =
          Array.isArray(parsedValue) && parsedValue.length > 0 ? parsedValue.join(', ') : '---';
        return '<span class="kachel-badge gray">' + displayValue + '</span>';
      }

      if (key === 'GruppeFlughaefen') {
        const parsedValue = typeof value === 'string' ? JSON.parse(value || '[]') : value;
        const displayValue =
          Array.isArray(parsedValue) && parsedValue.length > 0 ? parsedValue.join(', ') : '---';
        return '<span class="kachel-badge gray">' + displayValue + '</span>';
      }

      if (key === 'HinflugMaxWartezeit' || key === 'RueckflugMaxWartezeit') {
        return '<span class="kachel-badge gray">' + value + ' Min' + '</span>';
      }

      if (
        key === 'FruehesteAnkunft' ||
        key === 'SpaetesteAnkunft' ||
        key === 'FruehesterAbflug' ||
        key === 'SpaetesterAbflug' ||
        key === 'InlandsFruehesterAbflug' ||
        key === 'InlandsSpaetesteAnkunft'
      ) {
        return '<span class="kachel-badge gray">' + value.substring(0, 5) + ' Uhr' + '</span>';
      }
      if (key === 'State') {
        // Der numerische Code des Backends wird auf den State-Schlüssel abgebildet
        const stateMap = {
          100: 'Vorgeschlagen',
          104: 'Verworfen',
          105: 'CmBestaetigt',
          106: 'Anlagebereit',
          110: 'Aufgelegt',
          115: 'Abgesagt',
          130: 'Durchfuehrung',
          135: 'Durchgefuehrt',
          141: 'CmAngefragt',
          142: 'CmAbgelehnt',
        };
        const state = stateMap[value];
        if (!state) {
          return '<span class="kachel-badge gray">' + value + '</span>';
        }
        return reiseterminStatePillHtml(state, { festeBreite: false });
      }

      return '<span class="kachel-badge gray">' + value + '</span>';
    },
  },
};
</script>
<style scoped>
:deep(p) {
  padding: 0;
  margin-bottom: 0;
}

:deep(.kachel-badge) {
  height: auto;
  min-width: 0;
  padding: 6px 8px;
  font-size: 1rem;
  font-weight: 500;
  box-shadow: none;
}
</style>
